import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Phone, Gift } from 'lucide-react';
import { Button } from './ui/button';
import { CONTACT_INFO, WHATSAPP_MESSAGES } from '../data/mockData';

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const whatsappLink = `https://wa.me/${CONTACT_INFO.whatsapp}?text=${encodeURIComponent(WHATSAPP_MESSAGES.generic)}`;

  const navLinks = [
    { name: 'Services', href: '/deliveries', submenu: [
      { name: 'Deliveries', href: '/deliveries' },
      { name: 'Mini-Moves', href: '/mini-moves' },
    ]},
    { name: 'Areas', href: '/areas' },
    { name: 'Business', href: '/business' },
    { name: 'Track', href: '/track' },
    { name: 'Support', href: '/support' },
    { name: 'Contact', href: '/contact' }
  ];

  return (
    <>
      <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? 'bg-white shadow-md py-3' : 'bg-white/95 backdrop-blur-sm py-4'
      }`}>
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between">
            {/* Logo */}
            <Link to="/" className="flex items-center space-x-3">
              <img 
                src="https://customer-assets.emergentagent.com/job_47b545fa-5451-4dff-82ef-01203d831594/artifacts/dmupxk74_Rocket_Van_Express_Logo_Main_Logo_RGB.png" 
                alt="Rocket Van Express" 
                className="h-14 w-auto"
              />
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden lg:flex items-center space-x-8">
              {navLinks.map((link) => (
                <div key={link.name} className="relative group">
                  <Link
                    to={link.href}
                    className={`text-sm font-medium transition-colors hover:text-[#fb6402] ${
                      location.pathname === link.href ? 'text-[#fb6402]' : 'text-[#001A47]'
                    }`}
                  >
                    {link.name}
                  </Link>
                  {link.submenu && (
                    <div className="absolute top-full left-0 mt-2 bg-white shadow-lg rounded-lg py-2 min-w-[180px] opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all">
                      {link.submenu.map((item) => (
                        <Link
                          key={item.name}
                          to={item.href}
                          className="block px-4 py-2 text-sm text-[#001A47] hover:bg-gray-50 hover:text-[#fb6402] transition-colors"
                        >
                          {item.name}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </nav>

            {/* Desktop CTA */}
            <div className="hidden lg:flex items-center space-x-3">
              <Link to="/referral">
                <Button variant="ghost" size="sm" className="text-[#fb6402] hover:bg-[#fb6402]/10">
                  <Gift className="h-4 w-4 mr-1" />
                  Refer & Earn
                </Button>
              </Link>
              <a href={`tel:${CONTACT_INFO.phone}`}>
                <Button variant="outline" size="sm" className="border-[#001A47] text-[#001A47] hover:bg-[#001A47] hover:text-white">
                  <Phone className="h-4 w-4 mr-2" />
                  Call
                </Button>
              </a>
              <a href={whatsappLink} target="_blank" rel="noopener noreferrer">
                <Button size="sm" className="bg-[#fb6402] hover:bg-[#e55a02] text-white">
                  WhatsApp for Quote
                </Button>
              </a>
            </div>

            {/* Mobile Menu Toggle */}
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="lg:hidden text-[#001A47] p-2"
            >
              {isMobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="lg:hidden bg-white border-t">
            <nav className="container mx-auto px-4 py-4 space-y-2">
              {navLinks.map((link) => (
                <div key={link.name}>
                  <Link
                    to={link.href}
                    className="block py-2 text-[#001A47] hover:text-[#fb6402] font-medium"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    {link.name}
                  </Link>
                  {link.submenu && (
                    <div className="pl-4 space-y-2">
                      {link.submenu.map((item) => (
                        <Link
                          key={item.name}
                          to={item.href}
                          className="block py-1 text-sm text-gray-600 hover:text-[#fb6402]"
                          onClick={() => setIsMobileMenuOpen(false)}
                        >
                          {item.name}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              ))}
              {/* Mobile Referral Link */}
              <Link
                to="/referral"
                className="flex items-center py-2 text-[#fb6402] hover:text-[#e55a02] font-medium"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                <Gift className="h-4 w-4 mr-2" />
                Refer & Earn Free Deliveries
              </Link>
            </nav>
          </div>
        )}
      </header>

      {/* Spacer to prevent content from hiding under fixed header */}
      <div className="h-[72px]"></div>
    </>
  );
};

export default Header;
